'use client';

import { useState } from 'react';
import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { getNodesFromConnection } from '../../helpers/graphql';
import { getFishingWaterUrl } from '../../helpers/urls';
import {
  GetWatersInRegionBySpecies,
  GetWatersInRegionBySpeciesVariables,
  GetWatersInRegionBySpecies_entry_topFishingWaters_edges_node as IWaters,
} from '../../interfaces/graphql';
import { getWatersInRegionBySpecies } from '../../queries/rutilus/Regions';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { Pill } from '../Clickables/Pill/Pill';
import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { CloseIcon } from '../Icons/CloseIcon';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ISpeciesInfo } from '../Search/constants';
import { SearchSpecies } from '../Search/SearchSpecies/SearchSpecies';
import { Text } from '../Text/Text';

interface IProps {
  regionId: string;
  regionName: string | null;
}

export const TopWatersForRegionBySpecies = ({ regionId, regionName }: IProps) => {
  const [selectedSpecies, setSelectedSpecies] = useState<ISpeciesInfo | undefined>(undefined);
  const { data, loading: isLoading } = useRutilusQuery<
    GetWatersInRegionBySpecies,
    GetWatersInRegionBySpeciesVariables
  >(getWatersInRegionBySpecies, {
    variables: { id: regionId, speciesId: selectedSpecies?.speciesId },
    skip: !selectedSpecies,
  });
  const waters: IWaters[] = getNodesFromConnection(data?.entry.topFishingWaters);

  const title = `Top fishing spots${regionName ? ` in ${regionName}` : ''} for your favorite species`;

  return (
    <Container data-testid="TopWatersForRegionBySpecies">
      <div className="bg-lake-fog rounded-lg p-6 md:p-12 lg:mx-32">
        <div className="flex flex-col items-center md:px-12">
          <Heading as="h3" className="pb-3 text-center sm:mx-12" variant="md">
            {title}
          </Heading>

          <SearchSpecies onOptionSelect={setSelectedSpecies} className="w-full" />

          {selectedSpecies && (
            <div className="flex w-full items-start">
              <Pill
                variant="deepSea"
                className="my-3 flex md:my-5"
                size="sm"
                onClick={() => {
                  setSelectedSpecies(undefined);
                }}
              >
                <Text className="mr-2 mb-0">{selectedSpecies.displayName}</Text>
                <CloseIcon />
              </Pill>
            </div>
          )}
          {isLoading && <LoadingSpinner isMini />}
          {selectedSpecies && waters.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {waters.map(water => {
                const { catchesCount, displayName, _id: fishingWaterId } = water;

                const catchNumber = `${catchesCount} ${catchesCount > 1 ? ' total catches' : 'catch'}`;
                const explainText = `(all species)`;
                return (
                  // eslint-disable-next-line react/jsx-key
                  <InternalLink
                    to={getFishingWaterUrl(fishingWaterId, displayName)}
                    className="bg-sea-foam flex w-full items-center justify-between rounded-lg p-4"
                  >
                    <span className="flex flex-col">
                      <span className="mb-1 text-lg">{displayName}</span>
                      <span className="text-midnight text-sm">{catchNumber}</span>
                      <span className="text-midnight text-sm">{explainText}</span>
                    </span>

                    <ArrowRightIcon size={26} />
                  </InternalLink>
                );
              })}
            </div>
          )}
          {selectedSpecies && waters.length === 0 && (
            <div>
              <Text>
                There are no {selectedSpecies.displayName} catches in {regionName}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};
